import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {CustomerService} from './customer.service';
import {EmployeeService} from './employee.service';
import {environment} from '../../../environments/environment';

interface CallbackPromise {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

@Injectable({
  providedIn: 'root'
})
export class FlexPosService {
  private customerService = inject(CustomerService);
  private employeeService = inject(EmployeeService);

  private callbackPromises: Map<string, CallbackPromise[]> = new Map();

  customerOnJournal: WritableSignal<string | null> = signal(null);

  constructor() {
    this.initCallbackListeners();
  }

  async addCustomerToJournal(customerId: string) {
    this.keepAddingCustomerToJournal(customerId);
    await this.addCustomerNumberToJournalLine(customerId);
    return await this.getSelectedCustomerFromJournalLine();
  }

  async keepAddingCustomerToJournal(customerId: string, forceSync = false) {
    const selectedCustomerId = await this.getSelectedCustomer();
    if (!selectedCustomerId || selectedCustomerId !== customerId) {
      await this.addCustomerToJournalFlexpos(customerId);
    } else {
      return new Promise((resolve) => resolve(true));
    }

    if (forceSync) {
      await this.forceSyncCustomers();
    }

    return new Promise((resolve, reject) => {
      setTimeout( async () => {
        await this.keepAddingCustomerToJournal(customerId, true);
        resolve(true);
      }, 1000);
    });
  }

  async addCustomerNumberToJournalLine(customerNumber: string) {
    const alreadyExistingOrderLine = await this.getCustomerNumberOrderLine();
    if (alreadyExistingOrderLine) {
      await this.removeCustomerNumberFromOrderLine();
    }
    const orderLine = 'VISMACUSTOMERNO:' + customerNumber;
    return this.handleFlexPosFunction('addTextToJournal', [orderLine, 106]);
  }

  async removeCustomerNumberFromOrderLine() {
    const orderLine = await this.getCustomerNumberOrderLine();
    if (!orderLine) {
      return;
    }
    const journalLineUid = orderLine.UID;
    return this.handleFlexPosFunction('removeJournalLine', [journalLineUid]);
  }

  getJournalAndLines() {
    return this.handleFlexPosFunctionWithCallback('getJournalAndLines', [false], 'callbackJournalAndLines');
  }

  async getCustomerNumberOrderLine() {
    const journalAndLines = await this.getJournalAndLines();
    const journalAndLinesJson = JSON.parse(journalAndLines);
    return journalAndLinesJson.journal.journalLines?.find((line: any) => line.ShortDescr?.includes('VISMACUSTOMERNO:'))
  }

  async clearCustomer() {
    await this.handleFlexPosFunction('clearCustomer');
    await this.removeCustomerNumberFromOrderLine();
    return await this.getSelectedCustomerFromJournalLine();
  }

  async initSelectedCustomer() {
    const customerNo = await this.getSelectedCustomerFromJournalLine();
    if (customerNo) {
      this.customerService.selectCustomerByVismaId(customerNo);
    }
  }

  async getSelectedCustomer() {
    const customerJson = await this.handleFlexPosFunctionWithCallback(
      'getSelectedCustomerInfo',
      [],
      'callbackCustomerInfo'
    );

    const customerData = JSON.parse(customerJson).customer;
    this.customerOnJournal.set(customerData.CustomerNo);
    if (!customerData.CustomerNo) {
      return;
    }
    return customerData.CustomerNo;
  }

  async getSelectedCustomerFromJournalLine() {
    const customerOrderLine = await this.getCustomerNumberOrderLine();
    if (!customerOrderLine) {
      this.customerOnJournal.set(null);
      return;
    }
    const customerId = customerOrderLine?.ShortDescr?.split(':')[1] ?? null;
    this.customerOnJournal.set(customerId);
    return customerId;
  }


  closeBrowser() {
    return this.handleFlexPosFunction('closeBrowser');
  };

  forceSyncCustomers() {
    return this.handleFlexPosFunction('runPOSSync', [17]); // 17 is the customer sync job id
  }

  async getEmployee() {
    const employeeJson = await this.handleFlexPosFunctionWithCallback('getSelectedUserInfo', [], 'callbackUserInfo');
    const employeeData = JSON.parse(employeeJson).user;
    this.employeeService.setEmployee({
      id: employeeData.Uid,
      initials: employeeData.Initials,
      name: employeeData.Name
    });
  }


  private handleFlexPosFunction(func: string, funcData: any[] = []): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        if (environment.disableFlexPosEvents) {
          console.log('FlexPos events are disabled');
          this.fakeFlexPosFunction(func, funcData);
          resolve();
          return;
        }
        if (!window) {
          console.error('window not found');
          reject('window not found');
          return;
        }
        if (!(window as any)['CefSharp']) {
          console.error('CefSharp not found');
          reject('CefSharp not found');
          return;
        }

        const flexPosApi = await this.getFlexPOSBrowserAPIAsync();

        flexPosApi[func](...funcData);

        resolve();

      } catch (e) {
        console.log(e);
      }
    });
  }

  private fakeFlexPosFunction(func: string, funcData: any[]) {
    if (func === 'addCustomer') {
      this.customerOnJournal.set(funcData[0]);
      console.log('Customer added to journal', funcData[0]);
    } else if (func === 'clearCustomer') {
      this.customerOnJournal.set(null);
      console.log('Customer cleared');
    } else if (func === 'getSelectedCustomerInfo') {
      // Maybe set a fake customer here for testing
    } else if (func === 'getSelectedUserInfo') {
      // Do nothing
    } else if (func === 'closeBrowser') {
      alert('Browser closed');
    } else if (func === 'addTextToJournal') {
      const customerId = funcData[0].split(':')[1];
      this.customerOnJournal.set(customerId);
      console.log('Text added to journal', funcData);
    } else if (func === 'removeJournalLine') {
      this.customerOnJournal.set(null);
      console.log('Journal line removed');
    }
  }

  private async addCustomerToJournalFlexpos(customerId: string) {
    await this.handleFlexPosFunction('addCustomer', [customerId]);
  }

  private addFakeCallback(callbackName: string) {
    if (callbackName === 'callbackCustomerInfo') {
      (window as any)[callbackName](JSON.stringify({
        customer: {
          CustomerNo: this.customerOnJournal()
        }
      }));
    } else if (callbackName === 'callbackUserInfo') {
      (window as any)[callbackName](JSON.stringify({
        user: {
          Uid: 'fakeUid',
          Initials: 'FA',
          Name: 'Fake User'
        }
      }));
    } else if (callbackName === 'callbackJournalAndLines') {
      (window as any)[callbackName](JSON.stringify({
        journalLines: [
          this.customerOnJournal() ? {
            UID: 'fakeUid',
            ShortDescr: 'VISMACUSTOMERNO:' + this.customerOnJournal()
          } : {}
        ]
      }));
    }
  }

  private handleFlexPosFunctionWithCallback(func: string, funcData: any[], callbackName: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const promises = this.callbackPromises.get(callbackName) || [];
        promises.push({ resolve, reject });
        this.callbackPromises.set(callbackName, promises);

        await this.handleFlexPosFunction(func, funcData);

        if (environment.disableFlexPosEvents) {
          this.addFakeCallback(callbackName);
        }
      } catch (e) {
        console.log(e);
        reject(e);
      }
    });
  }


  private initCallbackListeners() {
    const callbacks = ['callbackCustomerInfo', 'callbackUserInfo', 'callbackJournalAndLines'];

    callbacks.forEach(callbackName => {
      (window as any)[callbackName] = (data: any) => {
        const promises = this.callbackPromises.get(callbackName) || [];
        promises.forEach(promise => promise.resolve(data));
        this.callbackPromises.set(callbackName, []);
      };
    });
  }

  private async getFlexPOSBrowserAPIAsync() {
    // @ts-ignore
    await CefSharp.BindObjectAsync('FlexPOS_BrowserAPIAsync');
    // @ts-ignore
    return FlexPOS_BrowserAPIAsync;
  }

}
